import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getCustomizedNavTitle } from 'lib/helpers';
import { PageContent, PageHeader } from 'components/page';
import { Box } from 'components/common/box';
import {
  CalendarAddEvent,
  CalendarEventCalendar,
  CalendarEventCalendarListView,
  CalendarEventModal,
  CalendarEventFilters
} from '.';

import type { CalendarApi } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import type { CalendarEventExtended } from 'shared-types';
import type { GetCalendarEventsRes } from 'shared-types/responses';

const Calendar = () => {
  const { dispatch } = useDispatch();

  const { language, messages, serverConfig, user } = useReduxState();

  const calendarApi = useRef<CalendarApi>(undefined);
  const calendarAddEvent = useRef<HTMLButtonElement>(undefined);
  const mouseClickTimeout = useRef<NodeJS.Timeout>(undefined);

  const router = useRouter();

  const [date, setDate] = useState(new Date());
  const [mouseClicks, setMouseClicks] = useState(0);
  const [selectedEvent, setSelectedEvent] =
    useState<CalendarEventExtended>(null);

  useEffect(() => {
    return () => clearTimeout(mouseClickTimeout.current);
  }, []);

  const getEvents = useCallback(
    async ({
      start,
      end
    }: {
      start?: Date;
      end?: Date;
    }): Promise<{ events: CalendarEventExtended[]; pages?: number }> => {
      const { category, page, search, status, type, view } = router.query;

      let url =
        '/admin/calendar-events?' +
        `&start=${start?.toISOString()}` +
        `&end=${end?.toISOString()}` +
        `&view=${view ? view : 'calendar'}` +
        `&timeFrame=${type ? type : 'current'}` +
        `&category=${category ? category : 'all'}` +
        `&status=${status ? status : 'all'}` +
        `&page=${page ? page : 1}`;
      if (search) url += `&search=${search}`;

      try {
        const response = await api.get<GetCalendarEventsRes>(url);

        return response.data;
      } catch (err) {
        await dispatch.handleErrorDispatch(
          err,
          `${messages.eventCalendarFetchError} ${messages.errorRefreshOrContact}`
        );
        return { events: [] };
      }
    },
    [router.query]
  );

  const fetchEventsForCalendarView = useCallback(
    async ({ start, end }: { start: Date; end: Date }) =>
      getEvents({ start, end }),
    [getEvents]
  );

  const fetchEventsForListView = useCallback(
    async () => getEvents({}),
    [getEvents]
  );

  const handleAddEventClose = () => {
    setDate(new Date()); // reset date
  };

  const handleDateClick = (info: DateClickArg) => {
    if (!hasCreatePermissions || !calendarAddEvent.current) return;

    clearTimeout(mouseClickTimeout.current);
    if (mouseClicks === 0) {
      setMouseClicks((value) => value + 1);
      mouseClickTimeout.current = setTimeout(() => setMouseClicks(0), 300);
      return;
    } else {
      setDate(info.date);
      setMouseClicks(0);
      mouseClickTimeout.current = setTimeout(
        () => calendarAddEvent.current.click(),
        0
      );
    }
  };

  const hasCreatePermissions =
    user?.roles.includes('superadmin') ||
    user?.permissions.includes('create-events');

  return (
    <div className="achievements">
      <PageHeader
        icon={['far', 'calendar-days']}
        title={getCustomizedNavTitle({
          defaultTitle: messages.eventCalendar,
          navLinksCustomTexts: serverConfig.navLinksCustomTexts,
          language,
          section: 'calendar'
        })}
      >
        {hasCreatePermissions ? (
          <CalendarAddEvent
            getRef={(el) => (calendarAddEvent.current = el)}
            initialDate={date}
            onClose={handleAddEventClose}
          />
        ) : null}
      </PageHeader>

      <PageContent>
        <Box headerContent={<CalendarEventFilters />} padding={0}>
          {!router.query.view || router.query.view === 'calendar' ? (
            <CalendarEventCalendar
              getEvents={fetchEventsForCalendarView}
              getCalendarApi={(api) => (calendarApi.current = api)}
              hasCreatePermissions={hasCreatePermissions}
              onDateClick={handleDateClick}
              onEventClick={(event) => setSelectedEvent(event)}
            />
          ) : (
            <CalendarEventCalendarListView
              getEvents={fetchEventsForListView}
              onEventClick={(event) => setSelectedEvent(event)}
            />
          )}
        </Box>

        <CalendarEventModal
          calendarApi={calendarApi.current}
          event={selectedEvent}
          setEvent={setSelectedEvent}
        />
      </PageContent>
    </div>
  );
};

export default Calendar;
